import { Quaternion, Vector2 } from 'three';

import type { CartesianPose, Plane } from '@sb/geometry';
import {
  applyCompoundPose,
  cameraPoseFromWristPose,
  CameraRelativeToWristInTooltipCoordinates,
  castCameraRay,
  findRayPlaneIntersection,
  getPlanePose,
  getTooltipOrientationPerpendicularToPlane,
  invertPose,
  movePlaneInItsZDirection,
  ZERO_POSE,
} from '@sb/geometry';
import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import type { CameraConfiguration } from '@sb/integrations/implementations/Camera/types';
import type { GPUTaskRunner } from '@sb/integrations/implementations/GPUTaskRunner/implementation/GPUTaskRunner';
import type { WristCamera } from '@sb/integrations/implementations/WristCamera/implementation/WristCamera';
import type { WristCameraConfiguration } from '@sb/integrations/implementations/WristCamera/types';
import type {
  CameraIntegration,
  CameraIntrinsics,
  CameraIntrinsicsExtended,
  ExtrinsicsKinematics,
} from '@sb/integrations/types/cameraTypes';
import { makeNamespacedLog } from '@sb/log';
import type {
  LocateMethod,
  RegionOfInterest,
  RoutineRunnerState,
  VisionInterface,
  Space,
  EquipmentInterface,
} from '@sb/routine-runner';
import { isNotUndefined } from '@sb/utilities';

import { getCalibrationOffset } from './calibrationHelpers';
import { getCameraColorFrame } from './helpers';
import type { Blob2D, TextPromptDetection2DParams } from './LocateTypes';
import { RunCheckpointBasedInferenceResponse } from './LocateTypes';

const log = makeNamespacedLog('runLocate');

/**
 * We allow users to define a tranform to offset locate results by a set amount relative to the plane.
 * This applies that transform to a blob
 */
function applyUserDefinedTransform(
  transform: CartesianPose,
  blobRelativeToBase: CartesianPose,
  planePoseRelativeToBase: CartesianPose,
): CartesianPose {
  // The user-defined tranform is relative to the plane, so get the
  // blobs in plane coordinates first, then apply the transform
  // and then transform back to base coordinates
  const blobRelativeToPlane = applyCompoundPose(
    blobRelativeToBase,
    invertPose(planePoseRelativeToBase),
  );

  // apply the rotation separately so it doesn't change the position
  const rotation = new Quaternion(
    blobRelativeToPlane.i,
    blobRelativeToPlane.j,
    blobRelativeToPlane.k,
    blobRelativeToPlane.w,
  );

  const offsetRotation = new Quaternion(
    transform.i,
    transform.j,
    transform.k,
    transform.w,
  );

  const newRotation = offsetRotation.multiply(rotation);

  const offsetBlob = {
    x: blobRelativeToPlane.x + transform.x,
    y: blobRelativeToPlane.y + transform.y,
    z: blobRelativeToPlane.z + transform.z,
    i: newRotation.x,
    j: newRotation.y,
    k: newRotation.z,
    w: newRotation.w,
  };

  const offsetBlobRelativeToBase = applyCompoundPose(
    offsetBlob,
    planePoseRelativeToBase,
  );

  return offsetBlobRelativeToBase;
}

/**
 * Takes 2D pixel coordinates from a camera image and returns
 * the 3D coordinates of the point in the base's coordinate system
 * */
function deproject(
  blobResult: { x: number; y: number; rotation: number },
  cameraIntrinsics: CameraIntrinsics | CameraIntrinsicsExtended,
  cameraPose: CartesianPose,
  plane: Plane,
): CartesianPose | undefined {
  const rayOrigin = {
    x: cameraPose.x,
    y: cameraPose.y,
    z: cameraPose.z,
  };

  const rayDirection = castCameraRay(
    cameraIntrinsics,
    cameraPose,
    new Vector2(blobResult.x, blobResult.y),
  );

  // Now that we have ray and base in same coordinate system
  // we can find the intersection of the ray with the plane
  const intersectionPoint = findRayPlaneIntersection(
    rayOrigin,
    rayDirection,
    plane,
  );

  if (intersectionPoint === undefined) {
    return undefined;
  }

  const tooltipPose = getTooltipOrientationPerpendicularToPlane(
    plane,
    blobResult.rotation,
  );

  const intersection: CartesianPose = {
    ...intersectionPoint,
    i: tooltipPose.x,
    j: tooltipPose.y,
    k: tooltipPose.z,
    w: tooltipPose.w,
  };

  return intersection;
}

function getCalibrationCorrection(
  pose: CartesianPose,
  calibration: Space.AccuracyCalibrationEntry[],
): CartesianPose {
  if (calibration.length === 0) {
    log.info('getCalibrationCorrection.none', 'No calibration data found');

    return ZERO_POSE;
  }

  const calibrationOffset = getCalibrationOffset(pose, calibration);

  log.info(
    'getCalibrationCorrection.offset',
    'Calibration offset',
    calibrationOffset,
  );

  return calibrationOffset;
}

interface RunLocateArgs {
  camera: {
    equipment: Camera | WristCamera | undefined;
    integration: CameraIntegration | undefined;
  };
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  plane: Plane;
  resultsLimit: number | undefined;
  transform: CartesianPose | null;
  accuracyCalibration: Space.AccuracyCalibrationEntry[] | null;
  vision: VisionInterface;
  objectHeight: number;
  getState: () => RoutineRunnerState;
  equipment: EquipmentInterface;
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined;
  extrinsics: ExtrinsicsKinematics | undefined;
}

export async function getCustomCheckpointBlobs({
  image,
  regionOfInterest,
  method,
  equipment,
  vision,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  method: LocateMethod;
  equipment: EquipmentInterface;
  vision: VisionInterface;
}): Promise<Blob2D[]> {
  if (method.kind !== 'CustomCheckpointDetection2D') {
    throw new Error('Expected CustomCheckpointDetection2D method');
  }

  // Get the GPUTaskRunner equipment
  const gpuTaskRunner = equipment.getDeviceById(
    method.settings.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${method.settings.gpuTaskRunnerId}`,
    );
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // Execute the RunCheckpointBasedInference command
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: method.settings.modelPath,
    model_type: method.settings.modelType,
    thresholding_params: method.settings.thresholdingParams,
  });

  // Parse and validate the response using Zod schema
  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'getCustomCheckpointBlobs',
      'Custom checkpoint inference returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Custom checkpoint inference returned invalid response format: ${parseResult.error.issues.map((i: any) => i.message).join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  // Handle error response
  if (validatedResponse.error) {
    log.error(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Custom checkpoint inference failed: ${validatedResponse.error.message}`,
    );
  }

  // Handle successful inference result
  if (validatedResponse.data) {
    const inferenceResult = validatedResponse.data;
    const { blobs } = inferenceResult;

    log.info(
      'getCustomCheckpointBlobs',
      `Custom checkpoint inference completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: inferenceResult.processingTimeMs,
        modelPath: inferenceResult.modelPath,
      },
    );

    // Convert the response blobs to the expected Blob2D format
    // Adjust coordinates back to original image space
    return blobs.map((blob: any) => ({
      x: blob.x + offsetX,
      y: blob.y + offsetY,
      width: blob.width,
      height: blob.height,
      rotation: blob.rotation,
      score: blob.score,
      contour: blob.contour,
    }));
  }

  throw new Error('Unexpected response type after validation');
}

export async function getTextPromptBlobs({
  image,
  regionOfInterest,
  params,
  equipment,
  vision,
}: {
  image: ArrayBuffer;
  regionOfInterest: RegionOfInterest;
  params: TextPromptDetection2DParams;
  equipment: EquipmentInterface;
  vision: VisionInterface;
}): Promise<Blob2D[]> {
  // If no GPU Task Runner is specified, use the vision service directly
  if (!params.gpuTaskRunnerId) {
    return vision.detect2DTextPrompt(image, regionOfInterest, params);
  }

  // Get the GPUTaskRunner equipment
  const gpuTaskRunner = equipment.getDeviceById(
    params.gpuTaskRunnerId,
  ) as GPUTaskRunner;

  if (!gpuTaskRunner) {
    throw new Error(
      `Could not find GPU Task Runner equipment with id ${params.gpuTaskRunnerId}`,
    );
  }

  // Preprocess the image using the vision service for ROI cropping
  const { croppedImage, offsetX, offsetY } = await vision.preprocessImage(
    image,
    regionOfInterest,
  );

  // Convert cropped image to base64
  const base64Image = Buffer.from(croppedImage).toString('base64');

  // Execute the text prompt detection via GPU Task Runner
  const response = await gpuTaskRunner.executeVisionCommand({
    command: 'run_inference',
    image: base64Image,
    model_path: 'text_prompt',
    model_type: 'TextPrompt',
    thresholding_params: {
      prompt: params.textInput,
      use_sam: params.useSam.toString(),
      box_threshold: params.boxThreshold.toString(),
      text_threshold: params.textThreshold.toString(),
    },
  });

  const parseResult = RunCheckpointBasedInferenceResponse.safeParse(response);

  if (!parseResult.success) {
    log.error(
      'getTextPromptBlobs',
      'Text prompt detection returned invalid response format',
      {
        response,
        parseError: parseResult.error.issues,
      },
    );

    throw new Error(
      `Text prompt detection returned invalid response format: ${parseResult.error.issues
        .map((issue: any) => issue.message)
        .join(', ')}`,
    );
  }

  const validatedResponse = parseResult.data;

  if (validatedResponse.error) {
    log.error(
      'getTextPromptBlobs',
      `Text prompt detection result validation failed: ${response}. Error: ${validatedResponse.error.message}`,
      { errorCode: validatedResponse.error.code },
    );

    throw new Error(
      `Text prompt detection result validation failed: ${validatedResponse.error.message}`,
    );
  }

  if (validatedResponse.data) {
    const { blobs } = validatedResponse.data;

    log.info(
      'getTextPromptBlobs',
      `Text prompt detection completed successfully, found ${blobs.length} blobs`,
      {
        processingTimeMs: validatedResponse.data.processingTimeMs,
      },
    );

    // Adjust coordinates back to original image space
    return blobs.map((blob: any) => ({
      ...blob,
      x: blob.x + offsetX,
      y: blob.y + offsetY,
    }));
  }

  throw new Error('Unexpected response type after validation');
}

async function get2DBlobs({
  camera,
  method,
  regionOfInterest,
  vision,
  equipment,
}: RunLocateArgs): Promise<Blob2D[]> {
  if (!camera.equipment) {
    throw new Error('Camera equipment is required for get2DBlobs');
  }

  const image = await getCameraColorFrame(camera.equipment, camera.integration);

  switch (method.kind) {
    case 'BlobDetection2D': {
      return vision.detect2DBlobs(image, regionOfInterest, method.settings);
    }
    case 'ShapeDetection2D': {
      return vision.detect2DShapes(
        image,
        method.templateImage,
        regionOfInterest,
        method.settings,
      );
    }
    case 'TextPromptDetection2D': {
      return getTextPromptBlobs({
        image,
        regionOfInterest,
        params: method.settings,
        equipment,
        vision,
      });
    }
    // This locates the center of a the chessboard used for accuracy calibration
    case 'ChessboardDetection2D': {
      const result = await vision.getChessboardCorners(image, 3, 3);

      return [
        {
          x: result.corners[4].x, // center corner
          y: result.corners[4].y, // center corner
          width: 1,
          height: 1,
          score: 0,
          rotation: 0,
          contour: [],
        },
      ];
    }
    case 'CustomCheckpointDetection2D': {
      return getCustomCheckpointBlobs({
        image,
        regionOfInterest,
        method,
        equipment,
        vision,
      });
    }
    default:
      throw new Error(`Unsupported method ${(method as LocateMethod).kind}`);
  }
}

async function getIntrinsics(
  intrinsics: CameraIntrinsics | CameraIntrinsicsExtended | undefined,
  equipment: Camera | WristCamera,
): Promise<CameraIntrinsics | CameraIntrinsicsExtended> {
  let out = intrinsics;

  if (!out) {
    const config = equipment.getConfig() as
      | CameraConfiguration
      | WristCameraConfiguration;

    out = await equipment.getIntrinsics();

    if (out == null) {
      log.error('getIntrinsics', 'No intrinsics found for camera', {
        config,
      });

      throw new Error(`No intrinsics found for camera ${config.name}`);
    }

    log.info('getIntrinsics', 'Using intrinsics from camera', {
      intrinsics: out,
      config,
    });
  } else {
    log.info('getIntrinsics', 'Using intrinsics from args', {
      intrinsics: out,
    });
  }

  return out;
}

async function getExtrinsics(
  extrinsics: ExtrinsicsKinematics | undefined,
  equipment: Camera | WristCamera,
): Promise<ExtrinsicsKinematics> {
  // If the camera is the built-in wrist camera, we always use the known
  // physical relationship between the camera and the flange (tcp)
  if (equipment.kind === 'WristCamera') {
    return {
      extrinsics: CameraRelativeToWristInTooltipCoordinates,
      kind: 'tcp',
    };
  }

  let out = extrinsics;

  if (!out) {
    const config = equipment.getConfig() as CameraConfiguration;

    const outRaw = equipment.getExtrinsics(true);

    if (outRaw == null) {
      log.error(
        'getExtrinsics',
        'No extrinsics found for camera when looking up extrinsics by ID',
        { config },
      );

      throw new Error(`No extrinsics found for camera ${config.name}`);
    }

    out = outRaw;
  } else {
    log.info('getExtrinsics', 'Using extrinsics from args', {
      extrinsics: out,
    });
  }

  return out;
}

export async function runLocate(args: RunLocateArgs): Promise<
  Array<{
    blob: Blob2D;
    pose: CartesianPose;
  }>
> {
  const blobs2D = await get2DBlobs(args);

  let cameraCorrection = ZERO_POSE;

  if (!args.camera.equipment) {
    throw new Error('Camera equipment is required for runLocate');
  }

  const intrinsics = await getIntrinsics(
    args.intrinsics,
    args.camera.equipment,
  );

  const extrinsics = await getExtrinsics(
    args.extrinsics,
    args.camera.equipment,
  );

  let cameraPose = extrinsics.extrinsics;

  if (extrinsics.kind === 'tcp') {
    const { wristPose } = args.getState().kinematicState;

    cameraPose = cameraPoseFromWristPose(wristPose, extrinsics.extrinsics);
  }

  log.info(
    'blobs',
    'Blobs detected',
    blobs2D.map((item) => ({ ...item, contour: undefined })),
  );

  blobs2D.sort((a, b) => b.score - a.score);

  const filteredBlobs2D = blobs2D.slice(0, args.resultsLimit ?? blobs2D.length);

  // Move plane in z direction by object height
  const heightAdjustedPlane = movePlaneInItsZDirection(
    args.plane,
    args.objectHeight,
  );

  const planePoseRelativeToBase = getPlanePose(heightAdjustedPlane);

  log.info(
    'Accuracy calibration',
    'Accuracy calibration',
    args.accuracyCalibration,
  );

  const blobsAndPoses = filteredBlobs2D
    .map((blob) => {
      let pose = deproject(blob, intrinsics, cameraPose, args.plane);

      if (!pose) {
        return undefined;
      }

      if (args.accuracyCalibration) {
        cameraCorrection = getCalibrationCorrection(
          pose,
          args.accuracyCalibration,
        );

        // Directly apply the calibration correction to the intersection point
        pose = applyCompoundPose(pose, cameraCorrection);
      }

      // apply user-defined offset and rotation
      if (args.transform) {
        pose = applyUserDefinedTransform(
          args.transform,
          pose,
          planePoseRelativeToBase,
        );
      }

      return { blob, pose };
    })
    .filter(isNotUndefined);

  log.info(
    'blobs.transformed',
    'Transformed blobs',
    blobsAndPoses.map((item) => ({
      ...item,
      blob: { ...item.blob, contour: undefined },
    })),
  );

  return blobsAndPoses;
}
